import { ArrowRight, Clock, Lock, Type, Plus, Pencil, MessageCircle, Paperclip, Move, Trash2, Archive } from 'lucide-react';
import LogIcon from '../../icons/LogIcon';
import useCardPanelStore from '../../../store/cardPanelStore';
import useBoardStore from '../../../store/boardStore';
import formatRelative, { formatAbsolute } from '../../../utils/formatRelative';

function Avatar({ name, url, size = 22 }) {
  const initials = name ? name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) : '?';
  return (
    <div
      className="rounded-full bg-accent/20 text-accent flex items-center justify-center font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {url ? <img src={url} alt={name} className="w-full h-full rounded-full object-cover" /> : initials}
    </div>
  );
}

function Badge({ label, color }) {
  if (label == null || label === '') return <span className="text-text-secondary/60 text-[11px]">—</span>;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold text-white truncate max-w-[140px]"
      style={{ backgroundColor: color || '#6b7280' }}
      title={label}
    >
      {label}
    </span>
  );
}

function PlainText({ text }) {
  if (text == null || text === '') return <span className="text-text-secondary/60 text-[11px]">—</span>;
  return <span className="text-[12px] text-text-primary truncate max-w-[180px]" title={String(text)}>{String(text)}</span>;
}

export default function ActivityTab() {
  const {
    activity, loadingActivity, activityError,
    activityHasMore, loadingMoreActivity, fetchMoreActivity, openCardId,
  } = useCardPanelStore();
  const users = useBoardStore((s) => s.users);
  const statusOptions = useBoardStore((s) => s.statusOptions);

  // M3c: id primeiro (formato canônico {id,label}), label cru como fallback
  // — mesma regra de BoardActivityModal.jsx. M4b: o snapshot de
  // card.status.changed (events) usa a chave `option_id`, não `id` (é um
  // instantâneo histórico — services/eventCatalog.js/cardValue.controller.js
  // #snapshotStatusValue —, diferente do valor canônico {id,label} que
  // card_values guarda hoje); aceita as duas.
  const colorFor = (columnId, raw, label) => {
    const opts = statusOptions?.[columnId] || [];
    const rawId = raw && typeof raw === 'object' ? (raw.option_id ?? raw.id) : null;
    if (rawId != null) {
      const byId = opts.find((o) => o.id === rawId);
      if (byId) return byId.color;
    }
    return opts.find((o) => o.label === label)?.color || null;
  };

  const fmtValue = (v, type) => {
    if (v == null || v === '') return null;
    if (type === 'person') return users.find((u) => u.id === v)?.name || `Usuário ${v}`;
    if (type === 'checkbox') return v ? 'Sim' : 'Não';
    if (typeof v === 'object') return v.label || v.url || JSON.stringify(v);
    return String(v);
  };

  const renderRow = (a) => {
    const d = a.details || {};
    const time = formatRelative(a.created_at);

    // M4b: nomes do catálogo novo (services/eventCatalog.js), lidos de
    // `events` — não mais os nomes legados de activity_log.
    switch (a.action) {
      case 'card.status.changed':
      case 'card.assignee.changed':
      case 'card.value.updated':
      case 'card.value.cleared': {
        const type = d.column_type;
        const fromLabel = fmtValue(d.from, type);
        const toLabel = fmtValue(d.to, type);
        const colored = type === 'status' || type === 'select';
        const fromColor = colored ? colorFor(d.column_id, d.from, fromLabel) : null;
        const toColor = colored ? colorFor(d.column_id, d.to, toLabel) : null;
        return {
          icon: colored ? <Pencil className="w-3.5 h-3.5 text-accent" /> : <Type className="w-3.5 h-3.5 text-text-secondary" />,
          label: d.column_name || 'Coluna',
          from: colored ? <Badge label={fromLabel} color={fromColor} /> : <PlainText text={fromLabel} />,
          to: colored ? <Badge label={toLabel} color={toColor} /> : <PlainText text={toLabel} />,
          time,
        };
      }
      case 'card.created':
        // divider_created virou card.created com is_divider em data (a
        // divisória é um cartão com outra cara na tela, não outra ação).
        return {
          icon: <Plus className={`w-3.5 h-3.5 ${d.is_divider ? 'text-text-secondary' : 'text-success'}`} />,
          label: d.is_divider ? 'Divisória criada' : 'Item criado',
          from: null,
          to: <PlainText text={d.target_name} />,
          time,
        };
      case 'card.renamed':
        return {
          icon: <Pencil className="w-3.5 h-3.5 text-accent" />,
          label: 'Nome',
          from: <PlainText text={d.from} />,
          to: <PlainText text={d.to} />,
          time,
        };
      case 'card.moved':
        return {
          icon: <Move className="w-3.5 h-3.5 text-accent" />,
          label: 'Movido',
          from: <PlainText text={d.from_group_name || `grupo ${d.from_group_id}`} />,
          to: <PlainText text={d.to_group_name || `grupo ${d.to_group_id}`} />,
          time,
        };
      case 'card.archived':
        return {
          icon: <Archive className="w-3.5 h-3.5 text-warning" />,
          label: 'Arquivado',
          from: null,
          to: <PlainText text={d.target_name} />,
          time,
        };
      case 'card.restored':
        return {
          icon: <Archive className="w-3.5 h-3.5 text-success" />,
          label: 'Restaurado',
          from: null,
          to: <PlainText text={d.target_name} />,
          time,
        };
      // Novo nesta fatia (M4b): antes invisível aqui — o INNER JOIN das
      // rotas legadas com `cards` apagava o histórico de um cartão junto
      // com o próprio cartão excluído.
      case 'card.deleted':
        return {
          icon: <Trash2 className="w-3.5 h-3.5 text-danger" />,
          label: 'Item excluído',
          from: null,
          to: <PlainText text={d.target_name} />,
          time,
        };
      case 'comment.created':
        // Sem preview de texto: events não guarda o conteúdo do comentário
        // (é editável/apagável pelo autor em card_comments — ver o
        // comentário em comment.routes.js). parent_id presente = resposta.
        return {
          icon: <MessageCircle className="w-3.5 h-3.5 text-accent" />,
          label: d.parent_id ? 'Resposta' : 'Atualização',
          from: null,
          to: <PlainText text={d.attachment_count ? `${d.attachment_count} anexo(s)` : ''} />,
          time,
        };
      case 'attachment.uploaded':
        return {
          icon: <Paperclip className="w-3.5 h-3.5 text-accent" />,
          label: 'Anexo adicionado',
          from: null,
          to: <PlainText text={d.original_name} />,
          time,
        };
      case 'attachment.deleted':
        return {
          icon: <Trash2 className="w-3.5 h-3.5 text-danger" />,
          label: 'Anexo removido',
          from: null,
          to: <PlainText text={d.original_name} />,
          time,
        };
      // Novo nesta fatia (M4b): um GRUPO INTEIRO mudando de quadro nunca
      // existiu em activity_log (card_id era obrigatório; a rotina antiga
      // pendurava isso no cartão mais antigo do grupo, sem ação própria).
      case 'group.moved':
        return {
          icon: <Move className="w-3.5 h-3.5 text-warning" />,
          label: 'Grupo movido',
          from: <PlainText text={d.from_board_name || `quadro ${d.from_board_id}`} />,
          to: <PlainText text={d.to_board_name || `quadro ${d.to_board_id}`} />,
          time,
        };
      default:
        return {
          icon: <Clock className="w-3.5 h-3.5 text-text-secondary" />,
          label: a.action,
          from: null,
          to: null,
          time,
        };
    }
  };

  if (loadingActivity) return <div className="p-6 text-sm text-text-secondary text-center">Carregando...</div>;

  if (activityError?.status === 403) {
    return (
      <div className="p-8 flex flex-col items-center justify-center h-full text-center">
        <Lock className="w-12 h-12 text-danger/40 mb-3" />
        <div className="text-sm font-semibold text-text-primary mb-1">Sem permissão para ver este histórico</div>
        <div className="text-xs text-text-secondary">Peça acesso a um administrador do quadro.</div>
      </div>
    );
  }

  if (activity.length === 0) {
    return (
      <div className="p-8 flex flex-col items-center justify-center h-full text-center">
        <LogIcon className="w-12 h-12 text-text-secondary/30 mb-3" />
        <div className="text-sm font-semibold text-text-primary mb-1">Nenhuma atividade ainda</div>
        <div className="text-xs text-text-secondary">Ações futuras aparecerão aqui.</div>
      </div>
    );
  }

  return (
    <div className="p-5 overflow-y-auto h-full">
      <div className="flex flex-col">
        {activity.map((a) => {
          const r = renderRow(a);
          return (
            <div
              key={a.id}
              className="flex items-center gap-3 py-2.5 border-b border-border/30 text-[12px] hover:bg-white/[0.02] transition-colors"
            >
              <div className="flex items-center gap-1.5 text-text-secondary shrink-0 w-[70px]">
                <Clock className="w-3 h-3" />
                <span className="text-[11px]" title={formatAbsolute(a.created_at)}>{r.time}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0 w-[170px]">
                <Avatar name={a.user_name} url={a.user_avatar} />
                <span className="text-text-primary truncate" title={a.user_name || 'Usuário'}>{a.user_name || 'Usuário'}</span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 w-[180px]">
                {r.icon}
                <span className="text-text-primary truncate" title={r.label}>{r.label}</span>
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {r.from !== null && (
                  <>
                    {r.from}
                    <ArrowRight className="w-3 h-3 text-text-secondary/60 shrink-0" />
                  </>
                )}
                {r.to}
              </div>
            </div>
          );
        })}
      </div>
      {activityHasMore && (
        <div className="pt-3 flex justify-center">
          <button
            onClick={() => fetchMoreActivity(openCardId)}
            disabled={loadingMoreActivity}
            className="text-xs font-medium text-accent hover:text-accent/80 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 rounded-md hover:bg-white/5 transition-colors"
          >
            {loadingMoreActivity ? 'Carregando...' : 'Carregar mais'}
          </button>
        </div>
      )}
    </div>
  );
}
